import React, { useState, useMemo, useEffect } from 'react';
import type { TimelineEntry } from '../../features/timeline';
import { uploadImage, getImageUrlById } from '../../api/images';
import ClassDescriptionEditor from './ClassDescriptionEditor';

interface TimelineModalProps {
  show: boolean;
  onHide: () => void;
  onTimelineSaved: () => void;
  item: TimelineEntry | null;
  userId: string;
}

// Generate year options from 1995 to current year + 1
const generateYearOptions = (): number[] => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let year = 1995; year <= currentYear + 1; year++) {
    years.push(year);
  }
  return years.reverse(); // Most recent first
};

// Helper to parse imageIds from JSON
const parseImageIds = (imageIdsJson: string | null | undefined): string[] => {
  if (!imageIdsJson) return [];
  try {
    const parsed = JSON.parse(imageIdsJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const TimelineModal: React.FC<TimelineModalProps> = ({
  show,
  onHide,
  onTimelineSaved,
  item,
  userId,
}) => {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [title, setTitle] = useState('');
  const [descriptionHtml, setDescriptionHtml] = useState('');
  const [imageIds, setImageIds] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const yearOptions = useMemo(() => generateYearOptions(), []);
  const isEditing = !!item;

  useEffect(() => {
    if (item) {
      setYear(item.year);
      setTitle(item.title);
      setDescriptionHtml(item.descriptionHtml || '');
      setImageIds(parseImageIds(item.imageIdsJson));
      setIsActive(item.isActive);
    } else {
      setYear(new Date().getFullYear());
      setTitle('');
      setDescriptionHtml('');
      setImageIds([]);
      setIsActive(true);
    }
    setError(null);
  }, [item, show]);

  const handleUploadImages = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    setIsUploadingImages(true);
    setError(null);
    try {
      const remainingSlots = 20 - imageIds.length;
      if (remainingSlots <= 0) {
        setError('You can upload up to 20 images.');
        return;
      }

      const filesToUpload = files.slice(0, remainingSlots);
      for (const file of filesToUpload) {
        const uploaded = await uploadImage(file);
        setImageIds((prev) => [...prev, uploaded.id]);
      }
    } catch {
      setError('Failed to upload one or more images. Please try again.');
    } finally {
      setIsUploadingImages(false);
      // Allow re-selecting the same files
      e.target.value = '';
    }
  };

  const removeImage = (index: number) => {
    setImageIds((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const { createTimelineEntry, updateTimelineEntry } = await import('../../features/timeline');

      const payload = {
        year,
        title,
        descriptionHtml: descriptionHtml || null,
        imageIdsJson: imageIds.length > 0 ? JSON.stringify(imageIds) : null,
        isActive,
      };

      if (isEditing && item) {
        await updateTimelineEntry(item.id, payload);
      } else {
        await createTimelineEntry(payload);
      }

      onTimelineSaved();
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save timeline entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-header border-0">
              <h5 className="modal-title">{isEditing ? 'Edit Timeline Entry' : 'Create Timeline Entry'}</h5>
              <button type="button" className="btn-close" onClick={onHide}></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                <div className="mb-3">
                  <label htmlFor="year" className="form-label">
                    Year <span className="text-danger">*</span>
                  </label>
                  <select
                    id="year"
                    className="form-select"
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    required
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">
                    The year determines the display order (earlier years appear first)
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="title" className="form-label">
                    Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    className="form-control"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., The Gathering, The First Call"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <i className="bi bi-text-paragraph me-2" /> Description
                  </label>
                  <ClassDescriptionEditor
                    key={item?.id ?? 'new'}
                    html={descriptionHtml}
                    onHtmlChange={setDescriptionHtml}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Images (Max 20)</label>
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {imageIds.map((imageId, index) => (
                      <div key={`${imageId}-${index}`} className="position-relative" style={{ width: 80, height: 60 }}>
                        <img
                          src={getImageUrlById(imageId)}
                          alt={`Timeline ${index + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            const img = e.currentTarget;
                            img.style.display = 'none';
                            const placeholder = img.parentElement?.querySelector(
                              '.timeline-modal-image-fallback'
                            ) as HTMLElement | null;
                            if (placeholder) placeholder.style.display = 'flex';
                          }}
                        />
                        <div
                          className="timeline-modal-image-fallback"
                          style={{
                            display: 'none',
                            position: 'absolute',
                            inset: 0,
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(255,255,255,0.85)',
                            borderRadius: 6,
                          }}
                        >
                          <i className="bi bi-image text-muted" />
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                          style={{ padding: '0.1rem 0.35rem' }}
                          onClick={() => removeImage(index)}
                          aria-label={`Remove image ${index + 1}`}
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    {imageIds.length === 0 && (
                      <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                        No images uploaded
                      </div>
                    )}
                  </div>

                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    multiple
                    disabled={isUploadingImages || isSubmitting}
                    onChange={handleUploadImages}
                  />

                  <small className="text-muted d-block mt-2">
                    {imageIds.length} of 20 images uploaded
                  </small>
                </div>

                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    id="isActive"
                    className="form-check-input"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <label htmlFor="isActive" className="form-check-label">
                    Active (visible on public timeline)
                  </label>
                </div>
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-secondary" onClick={onHide}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting || isUploadingImages}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>{isEditing ? 'Update' : 'Create'} Entry</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

    </>
  );
};

export default TimelineModal;
